import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { MenuItem } from 'primeng/api';
import { DataTable } from '../models/data-table.model';
import { VALIDATORS_KEY } from '@cores/utils/constants';
import * as lodash from 'lodash';

export function getErrorMessage(errorKey: string, errorValue: any, label: string = '') {
  switch (errorKey) {
    case 'required':
      return {
        key: VALIDATORS_KEY['required'],
        required: {
          label: !!label ? `"${label}"` : '',
        },
      };
    case 'pattern':
      return {
        key: VALIDATORS_KEY['pattern'],
        required: {
          label: !!label ? `"${label}"` : '',
        },
      };
    case 'phonePattern':
      return {
        key: VALIDATORS_KEY['phonePattern'],
        required: {},
      };
    case 'maxlength':
      return {
        key: VALIDATORS_KEY['maxlength'],
        required: {
          requiredLength: errorValue['requiredLength'],
        },
      };
    case 'minlength':
      return {
        key: VALIDATORS_KEY['minlength'],
        required: {
          requiredLength: errorValue['requiredLength'],
        },
      };
    case 'min':
      return {
        key: VALIDATORS_KEY['min'],
        required: {
          min: errorValue['min'],
        },
      };
    case 'max':
      return {
        key: VALIDATORS_KEY['max'],
        required: {
          max: errorValue['max'],
        },
      };
    case 'email':
      return {
        key: VALIDATORS_KEY['email'],
        required: {},
      };
    case 'datePattern':
      return {
        key: VALIDATORS_KEY['datePattern'],
        required: {
          datePattern: errorValue['requiredPattern'],
        },
      };
    case 'minDateAndMaxDate':
      return {
        key: VALIDATORS_KEY['minDateAndMaxDate'],
        required: {
          label: !!label ? `"${label}"` : '',
          minDate: errorValue['minDateLabel'],
          maxDate: errorValue['maxDateLabel'],
        },
      };
    case 'minDate':
      return {
        key: VALIDATORS_KEY['minDate'],
        required: {
          label: !!label ? `"${label}"` : '',
          minDateLabel: !!errorValue['minDateLabel'] ? `"${errorValue['minDateLabel']}"` : '',
        },
      };
    case 'maxDate':
      return {
        key: VALIDATORS_KEY['maxDate'],
        required: {
          label: !!label ? `"${label}"` : '',
          maxDateLabel: errorValue['maxDateLabel'] ? `"${errorValue['maxDateLabel']}"` : '',
        },
      };
    case 'maxDateToday':
      return {
        key: VALIDATORS_KEY['maxDateToday'],
        required: {
          label: !!label ? `"${label}"` : '',
        },
      };
    case 'minDateToday':
      return {
        key: VALIDATORS_KEY['minDateToday'],
        required: {
          label: !!label ? `"${label}"` : '',
        },
      };
    case 'minAtTheMoment':
      return {
        key: VALIDATORS_KEY['minAtTheMoment'],
        required: {
          label: !!label ? `"${label}"` : '',
        },
      };
    case 'requiredLength':
      return {
        key: VALIDATORS_KEY['requiredLength'],
        required: {
          label: !!label ? `"${label}"` : '',
          requiredLength: errorValue['requiredLength'],
        },
      };
    default:
      return {
        key: 'EMPTY',
        required: {},
      };
  }
}

export function isValidDate(dateValue: any) {
  return dateValue instanceof Date && !isNaN(dateValue.valueOf());
}

export function isEmpty(value: any) {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (!(value instanceof Date) && typeof value === 'object' && Object.keys(value).length === 0)
  );
}

export function isNotEmpty(value: any) {
  return !isEmpty(value);
}

export function cleanDataForm(formGroup: UntypedFormGroup | UntypedFormArray) {
  const form = formGroup;
  Object.keys(form.controls).forEach(field => {
    const control = form.get(field);
    if (control instanceof UntypedFormControl && typeof control.value === 'string') {
      control.setValue(control?.value?.trim(), { emitEvent: false });
    } else if (control instanceof UntypedFormGroup) {
      cleanDataForm(control);
    } else if (control instanceof UntypedFormArray) {
      for (const form of control.controls) {
        cleanDataForm(form as UntypedFormGroup);
      }
    }
  });
  return form.getRawValue();
}

export function validateAllFormFields(formGroup?: UntypedFormGroup | UntypedFormArray) {
  if (!formGroup) {
    return;
  }
  Object.keys(formGroup.controls).forEach(field => {
    const control = formGroup.get(field);
    if (control instanceof UntypedFormControl) {
      control.markAsTouched({ onlySelf: true });
      control.markAsDirty({ onlySelf: true });
    } else if (control instanceof UntypedFormGroup) {
      validateAllFormFields(control);
    } else if (control instanceof UntypedFormArray) {
      for (const form of control.controls) {
        validateAllFormFields(form as UntypedFormGroup);
      }
    }
  });
}

export function mapDataTable(data: any, params: any): DataTable {
  return {
    content: data?.data?.content || data?.content || [],
    currentPage: params?.page || 0,
    size: params?.size || 10,
    totalElements: data?.data?.totalElements || data?.totalElements || 0,
    totalPages: data?.data?.totalPages || data?.totalPages || 0,
    numberOfElements: data?.data?.numberOfElements || data?.numberOfElements || 0,
    first: (params?.size || 0) * (params?.page || 0),
  };
}

export function getNodeMenuByUrl(tree: any, url: string): any {
  let result = null;
  if (url === tree.routerLink) {
    return tree;
  } else if (tree.children) {
    for (let treeChild of tree.children) {
      result = getNodeMenuByUrl(treeChild, url);
      if (result) {
        break;
      }
    }
  }
  return result;
}

export function getUrlPathName(url: string) {
  return url.substring(0, url.indexOf('?') > -1 ? url.indexOf('?') : url.length);
}

export function arrayBufferToString(arrayBuffer: ArrayBuffer): string {
  const decoder = new TextDecoder('utf-8');
  const dataView = new DataView(arrayBuffer);
  const decodedString = decoder.decode(dataView);
  return decodedString;
}

export function buildBreadCrumb(
  route: ActivatedRoute,
  translate: TranslateService,
  url: string = '',
  breadcrumbs: MenuItem[] = []
): MenuItem[] {
  const labelKey = lodash.get(route, 'routeConfig.data.breadcrumb');
  const path = lodash.get(route, 'routeConfig.path', '');
  const hasUrl = lodash.get(route, 'routeConfig.data.hasUrl');
  const nextUrl = path ? `${url}/${path}` : url;
  if (lodash.isString(labelKey)) {
    const breadcrumb: MenuItem = {
      label: translate.instant(labelKey),
      routerLink: hasUrl ? nextUrl : undefined,
    };
    breadcrumbs.push(breadcrumb);
  }

  if (route.firstChild) {
    return buildBreadCrumb(route.firstChild, translate, nextUrl, breadcrumbs);
  }
  return breadcrumbs;
}

export function removeParamNullOrUndefined(params: any) {
  const newParams: any = {};
  lodash.forEach(params, (value, key) => {
    if (lodash.isNumber(value) || (!lodash.isNull(value) && !lodash.isUndefined(value) && !lodash.isEmpty(value))) {
      newParams[key] = value;
    }
  });
  return newParams;
}

export function convertToJson(value: string) {
  if (lodash.isString(value) && !lodash.isEmpty(value)) {
    return JSON.parse(value);
  }
  return null;
}

// Convert from Tree Data to Flat Data
export function flattenTreeData(data?: any[]): any[] {
  return lodash.reduce(
    data,
    (result: any[], current) => {
      return [...result, current, ...flattenTreeData(current.children)];
    },
    []
  );
}

export function mapTreeFolderView(items: any[], originCmId?: string) {
  if (items) {
    const list = items.map(item => {
      return { ...item, data: item, children: [] };
    });
    const root = list.filter((item: any) => !item.parentNode);
    root[0]['type'] = 'root';
    getChildrenNodeFolder(root[0], list);
    return root;
  }
  return [];
}

export function getChildrenNodeFolder(currentItem: any, items: any[]) {
  items.forEach(item => {
    if (item.parentNode === currentItem.node) {
      currentItem.children.push(item);
      getChildrenNodeFolder(item, items);
    }
  });
}

export function dataURItoBlob(dataURI: any, type?: string) {
  const byteString = window.atob(dataURI);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const int8Array = new Uint8Array(arrayBuffer);
  for (let i = 0; i < byteString.length; i++) {
    int8Array[i] = byteString.charCodeAt(i);
  }
  return new Blob([int8Array], { type: type || 'application/octet-stream' });
}

export function getValueDateTimeLocal(value: any): string | null {
  if (lodash.isDate(value)) {
    return moment(value).format(moment.HTML5_FMT.DATETIME_LOCAL);
  }
  return null;
}

export function popResponseError(err: any): string {
  return err.error?.message ? err.error.message : 'Có lỗi xảy ra';
}

export function convertFileSize(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function removeAscent(str: string) {
  if (str === null || str === undefined) return str;
  str = str.toLowerCase();
  str = str.replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a');
  str = str.replace(/[èéẹẻẽêềếệểễ]/g, 'e');
  str = str.replace(/[ìíịỉĩ]/g, 'i');
  str = str.replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o');
  str = str.replace(/[ùúụủũưừứựửữ]/g, 'u');
  str = str.replace(/[ỳýỵỷỹ]/g, 'y');
  str = str.replace(/đ/g, 'd');
  return str;
}
